import React, { ChangeEvent, JSX, useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  Field,
  FilterOp,
  FilterValueType,
  TableFilter,
  useDeleteTableFilterMutation,
  useUpdateTableFiltersMutation,
} from "gql/generated/types";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { InputNumber, InputNumberValueChangeEvent } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { OverlayPanel } from "primereact/overlaypanel";
import { ToggleButton } from "primereact/togglebutton";
import { TreeSelect } from "primereact/treeselect";
import { T } from "services/lang/LanguageService";
import { StringKey } from "services/lang/keys";
import { DEFAULT_DATE_FORMAT } from "utils/constants";
import { v4 as uuid } from "uuid";
import useProjectContext from "../hooks/useProjectContext";
import { ENUM_OPS, NUM_OPS } from "../utils/const";
import {
  FilterField,
  FilterInternal,
  MdmContainerName,
  MdmTableName,
  TableOptions,
} from "../utils/types";
import {
  apolloErrorToaster,
  buildNestedFields,
  findByKey,
  formatAndFilter,
  multiselectValue,
  showOptionDeviceStatus,
  showSelectedDeviceStatus,
  successToaster,
} from "../utils/utils";

type TableFiltersProps = {
  container: MdmContainerName;
  setTableOptions: React.Dispatch<React.SetStateAction<TableOptions>>;
  tableOptions: TableOptions;
  tableName: MdmTableName;
};

const TableFilters: React.FC<TableFiltersProps> = ({
  container,
  setTableOptions,
  tableOptions,
  tableName,
}) => {
  const { project, userSettings, projectId } = useProjectContext();
  const opRef = useRef<OverlayPanel>(null);
  const [filterTitle, setFilterTitle] = useState("");
  const [filters, setFilters] = useState<FilterInternal[]>([]);
  const [selectedFilterId, setSelectedFilterId] = useState<string | null>(null);
  const [filterValue, setFilterValue] = useState<string>("");
  const [saveFilter, { loading }] = useUpdateTableFiltersMutation({
    onCompleted: () => {
      successToaster(T("alert.filter-saved-successfully"));
      opRef.current?.hide();
      setFilterTitle("");
    },
    onError: apolloErrorToaster,
  });

  const [deleteSavedFilter, { loading: deleteLoading }] = useDeleteTableFilterMutation({
    onCompleted: () => successToaster(T("alert.filter-deleted-successfully")),
    onError: apolloErrorToaster,
  });

  const treeOptions = useMemo(
    () => buildNestedFields(container, project?.mdmProjectById.filterInfo.allFields as Field[]),
    [container, project?.mdmProjectById.filterInfo.allFields]
  );

  useEffect(() => {
    setFilters(formatAndFilter(tableOptions.andFilters, treeOptions));
    if (tableOptions.andFilters.length < 1) setSelectedFilterId(null);
  }, [tableOptions.andFilters, treeOptions]);

  const tableFilters = useMemo(
    () =>
      (userSettings?.userSettings.tableFilters?.filter(f => f?.tableName === tableName) ??
        []) as TableFilter[],
    [tableName, userSettings?.userSettings.tableFilters]
  );

  const getOptions = useCallback(
    (type: string) => {
      return project?.mdmProjectById.filterInfo.allFields.filter(f => f?.containerType === type);
    },
    [project?.mdmProjectById.filterInfo.allFields]
  );

  const addFilter = (e: DropdownChangeEvent) => {
    const filter = findByKey(treeOptions, e.value);
    setFilters(prev => [
      ...prev,
      {
        id: uuid(),
        value: null,
        cond: FilterOp.Eq,
        fields: filter?.data ?? [],
        label: filter?.data[0]?.label ?? "",
      },
    ]);
    setSelectedFilterId(null);
  };

  const onChange = (
    e: ChangeEvent<HTMLInputElement> | DropdownChangeEvent | InputNumberValueChangeEvent,
    filter: FilterInternal,
    value: any
  ) => {
    setFilters(prev =>
      prev.map(pf => (pf.id === filter.id ? { ...pf, [e.target.name]: value } : pf))
    );
    setSelectedFilterId(null);
  };

  const getOperators = useCallback(
    (filter: FilterField) => {
      const operators = project?.mdmProjectById.filterInfo.allOperators.map(op => ({
        ...op,
        label: T(op?.label as StringKey),
      }));
      if (
        (filter.containerType === "MdmDeviceConfiguration" && filter.key === "title") ||
        filter.key === "roleInObject" ||
        filter.key === "deviceType"
      ) {
        return operators?.filter(op => ENUM_OPS.includes(op?.key ?? ""));
      }
      switch (filter.inputType) {
        case FilterValueType.Enum:
        case FilterValueType.Boolean:
          return operators?.filter(op => ENUM_OPS.includes(op?.key ?? ""));
        case FilterValueType.Decimal:
        case FilterValueType.Integer:
        case FilterValueType.UtcDate:
          return operators?.filter(op => NUM_OPS.includes(op?.key ?? ""));
        case FilterValueType.String:
        default:
          return operators;
      }
    },
    [project?.mdmProjectById.filterInfo.allOperators]
  );

  const renderInputByType = useCallback(
    (parentFilter: FilterInternal, filter: FilterField) => {
      if (filter.containerType === "MdmDeviceConfiguration" && filter.key === "title") {
        const configNames = project?.mdmProjectById.deviceConfigs?.map(c => c?.title);

        return (
          <MultiSelect
            value={multiselectValue(parentFilter.value)}
            maxSelectedLabels={1}
            options={configNames}
            className="text-sm text-gray-700 flex-1 p-0"
            placeholder={T("word.select-one")}
            name="value"
            onChange={e => onChange(e, parentFilter, e.value)}
          />
        );
      }

      if (filter.key === "roleInObject" || filter.key === "deviceType") {
        const configNames =
          project?.mdmProjectById.configuration?.deviceConfiguration.measurementTypes?.filter(
            Boolean
          );
        return (
          <MultiSelect
            value={multiselectValue(parentFilter.value)}
            maxSelectedLabels={1}
            options={configNames}
            optionLabel="title"
            optionValue="id"
            className="text-sm text-gray-700 flex-1 p-0"
            placeholder={T("word.select-one")}
            name="value"
            onChange={e => onChange(e, parentFilter, e.value)}
          />
        );
      }

      switch (filter.inputType) {
        case FilterValueType.Enum:
          return (
            <MultiSelect
              value={multiselectValue(parentFilter.value)}
              maxSelectedLabels={1}
              options={filter.enum ?? []}
              className="text-sm text-gray-700 flex-1 p-0"
              optionLabel="label"
              optionValue="key"
              itemTemplate={filter.fieldType === "DeviceStatus" && showOptionDeviceStatus}
              selectedItemTemplate={
                parentFilter.value &&
                filter.fieldType === "DeviceStatus" &&
                showSelectedDeviceStatus
              }
              placeholder={T("word.select-one")}
              name="value"
              onChange={e => onChange(e, parentFilter, e.value)}
            />
          );
        case FilterValueType.UtcDate:
          return (
            <Calendar
              value={parentFilter.value ? new Date(parentFilter.value as string) : null}
              className="p-inputtext-sm flex-1 p-0 calendar-sm"
              name="value"
              onChange={e => onChange(e, parentFilter, e.value)}
              dateFormat={DEFAULT_DATE_FORMAT}
              showTime
              hourFormat="24"
            />
          );
        case FilterValueType.Boolean:
          return (
            <ToggleButton
              checked={parentFilter.value === "true"}
              className="p-inputtext-sm flex-1 p-0 m-0"
              name="value"
              onChange={e => onChange(e, parentFilter, e.value.toString())}
              onLabel="true"
              offLabel="false"
            />
          );
        case FilterValueType.Decimal:
        case FilterValueType.Integer:
          if (!parentFilter.value) {
            setFilters(prev =>
              prev.map(pf => (pf.id === parentFilter.id ? { ...pf, value: "0" } : pf))
            );
          }
          return (
            <InputNumber
              value={Number(parentFilter.value) ?? 0}
              className="p-inputtext-sm flex-1 p-0 pl-2"
              placeholder={`${T("action.write-value")}...`}
              maxFractionDigits={5}
              useGrouping={false}
              name="value"
              onValueChange={e => onChange(e, parentFilter, e.value?.toString())}
            />
          );
        case FilterValueType.String:
        default:
          return (
            <InputText
              value={(parentFilter.value as string) ?? ""}
              className="p-inputtext-sm flex-1 p-0 pl-2"
              placeholder={`${T("action.write-value")}...`}
              name="value"
              onChange={e => onChange(e, parentFilter, e.target.value)}
            />
          );
      }
    },
    [
      project?.mdmProjectById.configuration?.deviceConfiguration.measurementTypes,
      project?.mdmProjectById.deviceConfigs,
    ]
  );

  const handleSelectSavedFilter = (e: DropdownChangeEvent) => {
    setTableOptions(prev => ({
      ...prev,
      andFilters: e.value.andFilters,
    }));
    setFilters(formatAndFilter(e.value.andFilters, treeOptions));
    setSelectedFilterId(e.value.id);
  };

  const renderFieldSelector = useCallback(
    (filter: FilterInternal, filterIndex: number, currentLevel: number = 0): JSX.Element => {
      const opts = currentLevel === 0 ? [] : getOptions(filter.fields[currentLevel - 1].fieldType!);
      const needRecurse =
        (filter.fields[currentLevel] && !filter.fields[currentLevel].leaf) || false;
      return (
        <>
          {currentLevel > 0 && (
            <Dropdown
              value={filter.fields[currentLevel]}
              options={opts}
              optionLabel="label"
              valueTemplate={option =>
                T(option?.label ?? "word.select-one", { defaultValue: option?.label })
              }
              itemTemplate={option => T(option?.label, { defaultValue: option?.label })}
              placeholder={T("word.select-one")}
              name="fields"
              onChange={e =>
                onChange(e, filter, [...filter.fields.slice(0, currentLevel), e.value])
              }
              className="p-inputtext-sm flex-1 max-w-20rem"
            />
          )}
          {needRecurse && renderFieldSelector(filter, filterIndex, currentLevel + 1)}

          {filter.fields[currentLevel]?.leaf && (
            <div className="p-inputgroup p-inputtext-sm flex-1 max-w-30rem">
              {renderInputByType(filter, filter.fields[currentLevel])}
              <Dropdown
                value={filter?.cond}
                options={getOperators(filter.fields[currentLevel])}
                optionLabel="label"
                optionValue="key"
                placeholder={T("word.select-one")}
                name="cond"
                onChange={e => onChange(e, filter, e.value)}
                className="p-inputgroup-addon p-0"
              />
            </div>
          )}
        </>
      );
    },
    [getOperators, getOptions, renderInputByType]
  );

  const renderFilters = useMemo(
    () =>
      filters?.map((f, i) => (
        <div
          className="flex flex-column sm:flex-row align-items-baseline w-full gap-3 border-bottom-1 border-gray-300 py-1"
          key={f.id}
        >
          <span className="text-right">{T(f.label as StringKey, { defaultValue: f.label })}:</span>
          {renderFieldSelector(f, i + 1)}
          <Button
            className="p-button-text text-red-500"
            icon="pi pi-times"
            onClick={() => {
              setFilters(prev => prev.toSpliced(i, 1));
              setSelectedFilterId(null);
            }}
          />
        </div>
      )),
    [filters, renderFieldSelector]
  );

  const handleSetFilters = () => {
    const mapFilters = filters.map(f => ({
      cond: f.cond,
      property: f.fields.map(p => p.key).join("."),
      value: Array.isArray(f.value) ? f.value.join("|") : f.value,
    }));
    setTableOptions(prev => ({
      ...prev,
      first: 0,
      andFilters: mapFilters,
    }));
  };

  const handleSaveFilter = () => {
    saveFilter({
      variables: {
        projectId: projectId!,
        tableFilter: {
          andFilters: tableOptions.andFilters,
          title: filterTitle,
          tableName,
        },
      },
    });
  };

  const handleClearFilters = () => {
    setFilters([]);
    if (tableOptions.andFilters.length > 0) {
      setTableOptions(prev => ({
        ...prev,
        first: 0,
        andFilters: [],
      }));
    }
    setSelectedFilterId(null);
  };

  const handleDeleteSavedFilter = () => {
    if (!selectedFilterId) return;
    deleteSavedFilter({
      variables: {
        projectId: projectId!,
        filterId: selectedFilterId,
      },
      update(cache) {
        const normalizedId = cache.identify({ id: selectedFilterId, __typename: "TableFilter" });
        cache.evict({ id: normalizedId });
        cache.gc();
        setSelectedFilterId(null);
      },
    });
  };

  return (
    <div className="flex flex-column gap-2 m-3">
      {renderFilters}
      <div className="flex justify-content-between mt-3">
        <TreeSelect
          placeholder={T("action.add-filter")}
          options={treeOptions}
          className="w-10rem"
          onChange={addFilter}
          filter
          filterValue={filterValue}
          onFilterValueChange={e => setFilterValue(e.value)}
          onHide={() => setFilterValue("")}
          scrollHeight="400px"
        />
        {tableFilters.length > 0 && (
          <div>
            <Dropdown
              value={tableFilters.filter(f => f?.id === selectedFilterId)[0]}
              placeholder={T("action.select-saved-filter")}
              optionLabel="title"
              options={tableFilters ?? []}
              className="min-w-15rem"
              onChange={handleSelectSavedFilter}
              scrollHeight="200px"
            />
            {selectedFilterId && (
              <Button
                icon="pi pi-trash"
                title={T("action.delete-filter")}
                className="p-button-text text-red-500"
                loading={deleteLoading}
                onClick={handleDeleteSavedFilter}
              />
            )}
          </div>
        )}
        <div className="flex gap-2">
          <Button label={T("action.apply-filters")} className="w-9rem" onClick={handleSetFilters} />
          {tableOptions.andFilters.length > 0 && (
            <>
              <Button
                icon="pi pi-save"
                title={T("action.save-filter")}
                className="p-button-outlined"
                onClick={e => opRef.current?.toggle(e)}
              />
              <Button
                icon="pi pi-filter-slash"
                title={T("action.clear-filters")}
                className="p-button-outlined text-red-500"
                onClick={handleClearFilters}
              />
              <OverlayPanel ref={opRef}>
                <div className="p-inputgroup">
                  <InputText
                    value={filterTitle}
                    placeholder={T("word.filter-name")}
                    onChange={e => setFilterTitle(e.target.value)}
                    onKeyUp={e => e.key === "Enter" && handleSaveFilter()}
                    minLength={3}
                  />
                  <Button
                    label={T("action.save")}
                    type="button"
                    onClick={handleSaveFilter}
                    loading={loading}
                  />
                </div>
              </OverlayPanel>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TableFilters;
